import React, {
  Dispatch,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Alert, Button, Card, CardBody, CardTitle, Col, Row } from 'reactstrap';
import { DealerArea, DealerForm, DealerFullModel, DealerModel } from '../../../../shared/dealer';
import { FormNodeType, FormSectionModel } from '../../../../shared/kiosk';
import { Option } from '../../../../shared/options';
import { FormNodeMarkdown } from '../../../components/formElements/models/FormNodeMarkdown';
import { convertDealerToUpsert } from '../../../models/dealer';
import { DealerOption } from '../../../modules/dealer/form/DealerOption';
import { DealerPii } from '../../../modules/dealer/form/DealerPii';
import { SimpleLinkedTerms } from '../../../modules/dealer/form/SimpleLinkedTerms';
import { FormActionType } from '../../../modules/form/FormReducer';
import { useTranslation } from '../../../translations';
import { useConfig } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';

export interface DealerFormUser {
  id: number;
  addressCountry: string;
  addressState: string;
  addressLine1: string;
  addressLine2: string | null;
  addressCity: string;
  addressZipcode: string;
}

interface DealerFormProps {
  readonly dealer?: DealerFullModel;
  readonly form: DealerForm;
  readonly user: DealerFormUser;
  readonly prevDealer?: DealerFullModel;
  readonly callback: () => void;
}

export const DealerFormComponent: FC<DealerFormProps> = ({
  dealer: currentDealer,
  prevDealer,
  form,
  user,
  callback,
}) => {
  const { ts } = useTranslation();
  const { dealersAreas } = useConfig();
  const history = useHistory();
  const [saving, setSaving] = useState(false);

  const initialState = useMemo<DealerModel>(() => {
    if (currentDealer) {
      return convertDealerToUpsert(currentDealer);
    }

    if (prevDealer) {
      return convertDealerToUpsert(prevDealer);
    }

    return {
      name: '',
      email: '',
      addressCountry: user.addressCountry,
      addressState: user.addressState,
      addressLine1: user.addressLine1,
      addressLine2: user.addressLine2 ?? null,
      addressCity: user.addressCity,
      addressZipcode: user.addressZipcode,
      options: {},
    };
  }, [user, currentDealer, prevDealer]);

  const reducer = useCallback((old: DealerModel, action: FormActionType) => {
    switch (action.type) {
      case 'updateDealerPii': {
        const result: DealerModel = {
          ...old,
          [action.subType]: action.value,
        };

        return result;
      }

      case 'updateDealerOption': {
        const result: DealerModel = {
          ...old,
          options: {
            ...old.options,
            [action.optionId.toString()]: action.value,
          },
        };

        return result;
      }

      case 'setPolicyChecked': {
        /*
         * We don't actually need to handle this, as there is no check on the backend for the agreement.
         * Thiiiis might be a problem?
         */
        return old;
      }

      default: {
        throw new Error(
          `Unknown action type ${action.type}. Are you sure it should be called on the Dealer form?`,
        );
      }
    }
  }, []);

  const [dealer, setDealer] = useReducer(reducer, initialState);
  const [requiredCheckboxes, setRequiredCheckboxes] = useState<Record<string, boolean>>({});

  const logRequiredCheckbox = useCallback(
    (id: string, checked: boolean) => {
      setRequiredCheckboxes({
        ...requiredCheckboxes,
        [id]: checked,
      });
    },
    [setRequiredCheckboxes, requiredCheckboxes],
  );

  const [checkboxesDone, setCheckboxesDone] = useState(true);

  const submitForm = useCallback(async () => {
    if (!checkboxesDone) {
      toast.error(ts('not_all_required_agreements_have_been_checked_or_signed'));
      return;
    }

    setSaving(true);

    try {
      await api.updateDealer(user.id, dealer);
      setSaving(false);
      toast.success(ts('dealer_form.your_vendor_information_has_been'));
      callback();

      history.push(dealersAreas.length > 1 ? '/vendor' : `/vendor/space/${dealersAreas[0]!.id}`);
    } catch (error) {
      setSaving(false);
      captureError(error as Error);
    }
  }, [checkboxesDone, ts, user.id, dealer, callback, history, dealersAreas]);

  useEffect(() => {
    if (Object.keys(requiredCheckboxes).length > 0) {
      for (const box of Object.values(requiredCheckboxes)) {
        if (!box) {
          setCheckboxesDone(false);
          return;
        }
      }

      setCheckboxesDone(true);
    }
  }, [requiredCheckboxes]);

  return (
    <Row className="justify-content-center" id="dealerForm">
      <FormAlerts
        areas={dealersAreas}
        dealer={currentDealer}
        dealerAssistantLimit={form.dealerAssistantLimit}
        prevDealer={prevDealer}
      />
      {form.layout.sections.map((section) => (
        <DealerFormSectionRenderer
          currentDealer={currentDealer}
          dispatch={setDealer}
          key={section.id}
          logRequiredCheckbox={logRequiredCheckbox}
          options={form.options}
          section={section}
          state={dealer}
        />
      ))}
      <Col className="mt-10" lg={6} xs={12}>
        <Button
          block
          color="primary"
          disabled={saving || !checkboxesDone}
          id="continueApplication"
          onClick={submitForm}
        >
          {ts(saving ? 'common.saving' : 'common.continue')}
        </Button>
      </Col>
    </Row>
  );
};

interface DealerFormSectionRendererProps {
  readonly currentDealer?: DealerFullModel;
  readonly section: FormSectionModel;
  readonly state: DealerModel;
  readonly options: Option[];
  readonly dispatch: Dispatch<FormActionType>;
  readonly logRequiredCheckbox: (id: string, checked: boolean) => void;
}

const DealerFormSectionRenderer: FC<DealerFormSectionRendererProps> = ({
  section,
  state,
  options,
  currentDealer,
  dispatch,
  logRequiredCheckbox,
}) => {
  return (
    <Col className="mb-10" lg={8} xs={12}>
      <Card>
        <CardBody>
          <CardTitle>{section.name}</CardTitle>
          <Row>
            {section.nodes.map((node) => (
              <DealerFormNodeRenderer
                currentDealer={currentDealer}
                dispatch={dispatch}
                key={node.id}
                logRequiredCheckbox={logRequiredCheckbox}
                node={node}
                options={options}
                state={state}
              />
            ))}
          </Row>
        </CardBody>
      </Card>
    </Col>
  );
};

interface DealerFormNodeRendererProps {
  readonly currentDealer?: DealerFullModel;
  readonly node: FormNodeType;
  readonly state: DealerModel;
  readonly options: Option[];
  readonly dispatch: Dispatch<FormActionType>;
  readonly logRequiredCheckbox: (id: string, checked: boolean) => void;
}

const DealerFormNodeRenderer: FC<DealerFormNodeRendererProps> = ({
  node,
  state,
  options,
  currentDealer,
  dispatch,
  logRequiredCheckbox,
}) => {
  const hasCurrentDealer = !!currentDealer;

  const { ts } = useTranslation();

  switch (node.type) {
    case 'divider': {
      return (
        <Col xs="12">
          <hr />
        </Col>
      );
    }

    case 'markdown': {
      return <FormNodeMarkdown {...node} />;
    }

    case 'dealerPii': {
      return <DealerPii dispatch={dispatch} node={node} state={state} />;
    }

    case 'dealerOption': {
      const option = options.find((opt) => opt.id === node.optionId);

      if (!option) {
        return <pre> {ts('bad_node_type', node.type)} </pre>;
      }

      const isApproved = !!currentDealer?.applications.some((t) => t.status === 'approved');

      return (
        <DealerOption
          applicationOnly={node.applicationOnly ?? false}
          dealer={state}
          isApproved={isApproved}
          option={option}
          required={node.required ?? false}
          setDealer={dispatch}
        />
      );
    }

    case 'simpleLinkedTerms': {
      return (
        <SimpleLinkedTerms
          checked={hasCurrentDealer}
          disabled={hasCurrentDealer}
          dispatch={dispatch}
          logRequiredCheckbox={logRequiredCheckbox}
          node={node}
        />
      );
    }

    default: {
      return <pre> {ts('bad_node_type', node.type)} </pre>;
    }
  }
};

interface FormAlertsProps {
  readonly dealer?: DealerFullModel;
  readonly prevDealer?: DealerFullModel;
  readonly areas: DealerArea[];
  readonly dealerAssistantLimit: number;
}

const FormAlerts: FC<FormAlertsProps> = ({ areas, dealer, prevDealer, dealerAssistantLimit }) => {
  const { t, ts } = useTranslation();
  const alerts: React.JSX.Element[] = [];

  if (!dealer || dealer.applications.length === 0) {
    alerts.push(
      <FormAlertItem alertType="info" id="dealerNotSubmitted" key="dealerNotSubmitted">
        {ts('common.you_havent_yet_submitted_an')}
      </FormAlertItem>,
    );
  }

  if (!dealer && prevDealer) {
    alerts.push(
      <FormAlertItem alertType="info" id="dealerAutofilled" key="dealerAutofilled">
        {ts('dealer_form.weve_automatically_filled_in_some')}
      </FormAlertItem>,
    );
  }

  if (dealer) {
    for (const application of dealer.applications) {
      const area = areas.find((u) => u.id === application.areaId)!;

      switch (application.status) {
        case 'pending': {
          alerts.push(
            <FormAlertItem alertType="info" id="dealerAppPending" key="dealerAppPending">
              <CardTitle>
                {t('dealer_form.has_not_been_approved_yet', <AreaLink area={area} />)}
              </CardTitle>
              {ts('dealer_form.please_be_patient_your_application')}
            </FormAlertItem>,
          );

          break;
        }

        case 'waitlisted': {
          alerts.push(
            <FormAlertItem alertType="info" id="dealerAppWaitlisted" key="dealerAppWaitlisted">
              <CardTitle>{t('dealer_form.is_waitlisted', <AreaLink area={area} />)}</CardTitle>
              {ts('dealer_form.your_application_will_be_reviewed')}
            </FormAlertItem>,
          );

          break;
        }

        case 'rejected': {
          alerts.push(
            <FormAlertItem alertType="info" id="dealerAppRejected" key="dealerAppRejected">
              <CardTitle>{t('dealer_form.was_rejected', <AreaLink area={area} />)}</CardTitle>
              {ts('dealer_form.application_has_been_rejected')}
            </FormAlertItem>,
          );

          break;
        }

        case 'cancelled': {
          alerts.push(
            <FormAlertItem alertType="warning" id="dealerAppCancelled" key="dealerAppCancelled">
              <CardTitle>{ts('dealer_form.was_cancelled')}</CardTitle>
              {ts('dealer_form.application_has_been_rejected')}
            </FormAlertItem>,
          );

          break;
        }

        case 'approved': {
          if (application.paidOrderItemId) {
            alerts.push(
              <FormAlertItem alertType="success" id="dealerAppPaid" key="dealerAppPaid">
                <CardTitle>{ts('common.your_application_has_been_approved')}</CardTitle>
                {application.tableNumber ? (
                  <>
                    {ts('dealer_form.your_table_assignment_for')} <AreaLink area={area} />{' '}
                    <strong>{application.tableNumber}</strong>.{' '}
                    {ts('dealer_form.this_position_may_be_subject_to_change_before_the_convention')}
                  </>
                ) : (
                  <>
                    {ts('dealer_form.table_not_assigned')}{' '}
                    {ts('dealer_form.this_will_be_confirmed_closer_to_the_convention')}
                  </>
                )}
                {dealerAssistantLimit > 0 && (
                  <>
                    <hr />
                    <Link to="/vendor/assistants">{ts('dealer_form.click_here')}</Link>{' '}
                    {ts('dealer_form.to_manage_assistants')}
                  </>
                )}
              </FormAlertItem>,
            );
          } else {
            alerts.push(
              <FormAlertItem
                alertType="warning"
                id="dealerAppOutstanding"
                key="dealerAppOutstanding"
              >
                <CardTitle>{ts('dealer_form.application_approved', area.name)}</CardTitle>
                {ts('dealer_form.you_have_been_conditionally_approved')}
                <br />
                <br />
                <Button block color="primary" outline tag={Link} to="/pay">
                  {ts('dealer_form.pay_for_table')}
                </Button>
              </FormAlertItem>,
            );
          }
        }
      }
    }
  }

  if (areas.every((u) => u.full)) {
    alerts.push(
      <FormAlertItem alertType="danger" id="dealerFull" key="dealerFull">
        <CardTitle>{ts('dealer_form.all_dealer_areas_are_currently')}</CardTitle>
        {ts('dealer_form.you_may_continue_to_submit')}
      </FormAlertItem>,
    );
  }

  return alerts;
};

const FormAlertItem: FC<{
  readonly id: string;
  readonly alertType: string;
  readonly children?: ReactNode;
}> = ({ id, alertType, children }) => {
  return (
    <Col id={id} lg={8} xs={12}>
      <Alert color={alertType}>{children}</Alert>
    </Col>
  );
};

const AreaLink: FC<{ readonly area: DealerArea }> = ({ area }) => {
  return <Link to={`/vendor/space/${area.id}`}>{area.name}</Link>;
};
